/**
 * User Service
 * Handles all user management API operations
 * 
 * @module services/userService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * User Service Class
 * Provides methods for user management operations
 * 
 * @class UserService
 * @extends BaseService
 * 
 * @example
 * import userService from './services/userService';
 * 
 * // Get all users
 * const users = await userService.getAll({ page: 1, limit: 10 });
 * 
 * // Create user
 * const newUser = await userService.create({
 *   name: 'Mamadou Diallo',
 *   email: 'mamadou@ecomotech.com',
 *   role: 'admin'
 * });
 */
class UserService extends BaseService {
  constructor() {
    super('/users');
  }

  /**
   * Get all users with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=10] - Items per page
   * @param {string} [params.role] - Filter by role
   * @param {string} [params.department] - Filter by department
   * @param {string} [params.status] - Filter by status (active, inactive)
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getAll(params = {}) {
    return this.get('', { params });
  }

  /**
   * Get user by ID
   * 
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User object
   */
  async getById(userId) {
    return this.get(`/${userId}`);
  }

  /**
   * Create new user
   * 
   * @param {Object} userData - User data
   * @param {string} userData.name - Full name
   * @param {string} userData.email - Email address
   * @param {string} userData.password - Password
   * @param {string} userData.role - User role
   * @param {string} [userData.department] - Department
   * @param {string} [userData.position] - Job position
   * @param {string} [userData.phone] - Phone number
   * @param {string} [userData.avatar] - Avatar URL
   * @returns {Promise<Object>} Created user
   */
  async create(userData) {
    return this.post('', userData);
  }

  /**
   * Update user
   * 
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user
   */
  async update(userId, userData) {
    return this.put(`/${userId}`, userData);
  }

  /**
   * Partial update user
   * 
   * @param {string} userId - User ID
   * @param {Object} updates - Partial updates
   * @returns {Promise<Object>} Updated user
   */
  async partialUpdate(userId, updates) {
    return this.patch(`/${userId}`, updates);
  }

  /**
   * Delete user
   * 
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async delete(userId) {
    return this.delete(`/${userId}`);
  }

  /**
   * Search users with cancellation support
   * Perfect for autocomplete and search-as-you-type
   * 
   * @param {string} query - Search query
   * @param {Object} [options={}] - Search options
   * @param {string} [options.role] - Filter by role
   * @param {string} [options.department] - Filter by department
   * @param {number} [options.limit=10] - Max results
   * @returns {Promise<Array>} Matching users
   */
  async search(query, options = {}) {
    return this.getWithCancel('/search', 'user-search', {
      params: { q: query, ...options }
    });
  }

  /**
   * Get users by role
   * 
   * @param {string} role - Role name (admin, executive, department_manager, staff, viewer)
   * @param {Object} [params={}] - Additional query parameters
   * @returns {Promise<Array>} Users with specified role
   */
  async getByRole(role, params = {}) {
    return this.get('/by-role', { params: { role, ...params } });
  }

  /**
   * Get users by department
   * 
   * @param {string} department - Department name
   * @param {Object} [params={}] - Additional query parameters
   * @returns {Promise<Array>} Users in specified department
   */
  async getByDepartment(department, params = {}) {
    return this.get('/by-department', { params: { department, ...params } });
  }

  /**
   * Update user role
   * 
   * @param {string} userId - User ID
   * @param {string} newRole - New role
   * @returns {Promise<Object>} Updated user
   */
  async updateRole(userId, newRole) {
    return this.patch(`/${userId}/role`, { role: newRole });
  }

  /**
   * Update user permissions
   * 
   * @param {string} userId - User ID
   * @param {Array<string>} permissions - Array of permission names
   * @returns {Promise<Object>} Updated user
   */
  async updatePermissions(userId, permissions) {
    return this.patch(`/${userId}/permissions`, { permissions });
  }

  /**
   * Activate user
   * 
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Updated user
   */
  async activate(userId) {
    return this.patch(`/${userId}/activate`);
  }

  /**
   * Deactivate user
   * 
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Updated user
   */
  async deactivate(userId) {
    return this.patch(`/${userId}/deactivate`);
  }

  /**
   * Reset user password
   * 
   * @param {string} userId - User ID
   * @param {Object} passwordData - Password data
   * @param {string} passwordData.newPassword - New password
   * @param {boolean} [passwordData.requireChange=true] - Require password change on next login
   * @returns {Promise<void>}
   */
  async resetPassword(userId, passwordData) {
    return this.post(`/${userId}/reset-password`, passwordData);
  }

  /**
   * Get user activity log
   * 
   * @param {string} userId - User ID
   * @param {Object} [params={}] - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.startDate] - Start date filter
   * @param {string} [params.endDate] - End date filter
   * @returns {Promise<Array>} Activity log entries
   */
  async getActivityLog(userId, params = {}) {
    return this.get(`/${userId}/activity`, { params });
  }

  /**
   * Get user sessions
   * 
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Active sessions
   */
  async getSessions(userId) {
    return this.get(`/${userId}/sessions`);
  }

  /**
   * Revoke user session
   * 
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID
   * @returns {Promise<void>}
   */
  async revokeSession(userId, sessionId) {
    return this.delete(`/${userId}/sessions/${sessionId}`);
  }

  /**
   * Revoke all user sessions (force logout)
   * 
   * @param {string} userId - User ID
   * @returns {Promise<void>}
   */
  async revokeAllSessions(userId) {
    return this.delete(`/${userId}/sessions`);
  }

  /**
   * Bulk operations
   * 
   * @param {Object} bulkData - Bulk operation data
   * @param {Array<string>} bulkData.userIds - Array of user IDs
   * @param {string} bulkData.action - Action to perform (activate, deactivate, delete, updateRole)
   * @param {Object} [bulkData.data] - Additional data for the action
   * @returns {Promise<{success: number, failed: number, results: Array}>}
   */
  async bulkOperation(bulkData) {
    return this.post('/bulk', bulkData);
  }

  /**
   * Export users data
   * 
   * @param {Object} [params={}] - Export parameters
   * @param {string} [params.format='csv'] - Export format (csv, xlsx, pdf)
   * @param {Array<string>} [params.fields] - Fields to include
   * @param {Object} [params.filters] - Filters to apply
   * @returns {Promise<Blob>} Export file
   */
  async export(params = {}) {
    return this.get('/export', { 
      params,
      responseType: 'blob'
    });
  }

  /**
   * Import users from file
   * 
   * @param {File} file - CSV or Excel file
   * @param {Object} [options={}] - Import options
   * @param {boolean} [options.updateExisting=false] - Update existing users
   * @param {boolean} [options.skipErrors=true] - Skip rows with errors
   * @returns {Promise<{imported: number, updated: number, failed: number, errors: Array}>}
   */
  async import(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    
    return this.post('/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Get user statistics
   * 
   * @returns {Promise<Object>} User statistics
   * @returns {number} return.total - Total users
   * @returns {number} return.active - Active users
   * @returns {number} return.inactive - Inactive users
   * @returns {Object} return.byRole - Users count by role
   * @returns {Object} return.byDepartment - Users count by department
   */
  async getStatistics() {
    return this.get('/statistics');
  }

  /**
   * Validate email availability
   * 
   * @param {string} email - Email to validate
   * @param {string} [excludeUserId] - User ID to exclude (for updates)
   * @returns {Promise<{available: boolean}>}
   */
  async validateEmail(email, excludeUserId = null) {
    return this.get('/validate-email', {
      params: { email, excludeUserId }
    });
  }
}

// Export singleton instance
const userService = new UserService();
export default userService;
